import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subscription, take} from 'rxjs';
import {MatDialog} from '@angular/material/dialog';
import {AlertDialogComponent} from 'src/app/shared/components/alert-dialog/alert-dialog.component';
import {DigoService} from 'src/app/services/digo.service';
import {FirebaseService} from 'src/app/services/firebase.service';
import {Timestamp} from 'firebase/firestore';
import {filter, finalize, tap} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import {ChatSoporteService} from 'src/app/home/services/chat-soporte.service';
import {ChatCoordinatorService} from 'src/app/home/services/chat-coordinator.service';


@Component({
    selector: 'app-chat-soporte',
    templateUrl: './chat-soporte.component.html',
    styleUrls: ['../chat/chat.component.css', './chat-soporte.component.css'],
    standalone: false
})
export class ChatSoporteComponent implements OnInit, OnDestroy {
    @ViewChild('chatWidget') chatWidget!: ElementRef<HTMLDivElement>;
    @ViewChild('resizer') resizer!: ElementRef<HTMLDivElement>;

    public isOpen = false;
    public isMinimized = false;
    public chatId?: string | null;
    public pdv: any;
    public newMessage = '';
    public pdvId: any;
    public chatIdLoaded = false;
    public chatExists = false;
    public chatUnderConstruction = false;
    public showNewMessageToast = false;
    private toastTimer?: any;
    private unsubscribeChat?: () => void;
    private unsubscribeMessages?: () => void;
    private realtimeReady = false;
    public selectedFile: File | null = null;
    public unreadMessages = 0;
    private newMessageReceived = false;
    private messageToDelete: string | null = null;
    private subscriptions: Subscription[] = [];

    public readonly config = this.chatSoporteService.config;

    private startX = 0;
    private startY = 0;
    private startWidth = 0;
    private startHeight = 0;
    private newWidth = 0;
    private newHeight = 0;

    messages: Array<any> = [];

    constructor(
        private firebaseService: FirebaseService,
        public chatSoporteService: ChatSoporteService,
        private chatCoordinator: ChatCoordinatorService,
        private digoService: DigoService,
        public dialog: MatDialog
    ) {
        this.subscriptions.push(
            this.digoService.getPDV().pipe(
                filter(pdv => !!pdv),
                tap(pdv => {
                    this.pdv = pdv;
                    this.pdvId = pdv.info.idpdv;
                })
            ).subscribe(() => {
                this.getChatId();
            })
        );
    }

    ngOnInit() {
        this.subscriptions.push(
            this.chatSoporteService.requestToggleChat$.subscribe(() => {
                if (this.chatUnderConstruction) {
                    return;
                }
                this.toggle();
            }),
            this.chatCoordinator.closeChannel$.subscribe((channel) => {
                if (channel === 'soporte') {
                    this.forceClose();
                }
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        if (this.unsubscribeChat) {
            this.unsubscribeChat();
        }
        if (this.unsubscribeMessages) {
            this.unsubscribeMessages();
        }
        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
        }
    }

    initResize(event: MouseEvent) {
        event.preventDefault();

        if (!this.chatWidget) {
            return;
        }

        this.startX = event.clientX;
        this.startY = event.clientY;
        this.startWidth = this.chatWidget.nativeElement.offsetWidth;
        this.startHeight = this.chatWidget.nativeElement.offsetHeight;
        this.newWidth = this.startWidth;
        this.newHeight = this.startHeight;

        window.addEventListener('mousemove', this.resize);
        window.addEventListener('mouseup', this.stopResize);
    }

    resize = (event: MouseEvent) => {
        if (!this.chatWidget) {
            return;
        }

        const dx = this.startX - event.clientX;
        const dy = this.startY - event.clientY;

        this.newWidth = this.startWidth + dx;
        this.newHeight = this.startHeight + dy;

        this.newWidth = Math.max(420, Math.min(700, this.newWidth));
        this.newHeight = Math.max(520, Math.min(1000, this.newHeight));

        this.chatWidget.nativeElement.style.width = this.newWidth + 'px';
        this.chatWidget.nativeElement.style.height = this.newHeight + 'px';
    };

    stopResize = () => {
        window.removeEventListener('mousemove', this.resize);
        window.removeEventListener('mouseup', this.stopResize);
    };

    public getChatId(): void {
        this.chatSoporteService.getPdvChat(this.pdvId)
            .pipe(take(1))
            .subscribe({
                next: (resp: any) => {
                    const id = resp?.msg?.id ?? (typeof resp?.msg === 'string' ? resp.msg : null) ?? resp?.id;
                    const archived = resp?.msg?.archived === true || resp?.archived === true;

                    if (id != null && !archived) {
                        this.chatId = id;
                        this.chatExists = true;
                        this.chatIdLoaded = true;
                        this.chatSoporteService.setChatArchived(false);
                        this.startRealtimeMessages();
                    } else if (archived) {
                        this.chatExists = false;
                        this.chatIdLoaded = true;
                        this.chatSoporteService.setChatArchived(true);
                    } else {
                        // 200 sin chat: listo para crear al abrir
                        this.chatExists = false;
                        this.chatIdLoaded = true;
                    }
                },
                error: (error) => {
                    this.chatExists = false;
                    this.chatIdLoaded = true;

                    console.error('Error al obtener el chat de soporte: ', error.status, error);

                    // 404/409 = aún no existe → se creará al abrir
                    if (error.status == 409 || error.status == 404) {
                        this.startRealtimeNewChatListener();
                    }
                }
            });
    }

    private createChat() {
        this.chatUnderConstruction = true;
        this.chatSoporteService.createChatRemote(this.pdvId).pipe(
            take(1),
            finalize(() => {
                this.chatUnderConstruction = false;
            })
        ).subscribe({
            next: (resp: any) => {
                const id = resp?.msg?.id ?? (typeof resp?.msg === 'string' ? resp.msg : null) ?? resp?.id ?? resp?.msg;
                if (id == null) {
                    this.dialog.open(AlertDialogComponent, {
                        width: '420px',
                        data: {message: 'No se pudo crear el chat de soporte. Respuesta inválida del servidor.', className: ''}
                    });
                    return;
                }
                this.chatId = id;
                this.chatExists = true;
                this.chatIdLoaded = true;
                this.chatSoporteService.setChatArchived(false);
                this.startRealtimeMessages();
                this.toggle();
            },
            error: (error: HttpErrorResponse) => {
                this.chatExists = false;
                this.chatIdLoaded = true;
                const backendMsg = error?.error?.error || error?.error?.message || error?.message || '';
                this.dialog.open(AlertDialogComponent, {
                    width: '420px',
                    data: {
                        message: `No se pudo abrir el chat de soporte.${backendMsg ? '<br/><br/><small>' + backendMsg + '</small>' : ''}`,
                        className: ''
                    }
                });
            }
        });
    }

    public toggle(): void {
        if (!this.chatIdLoaded) {
            this.dialog.open(AlertDialogComponent, {
                width: '420px',
                data: {message: 'Aún se está cargando los datos relacionados al chat de soporte.', className: ''}
            });
            return;
        }

        // Si no existe, crearlo (misma operativa que Dist)
        if (!this.chatExists) {
            this.createChat();
            return;
        }

        this.isOpen = !this.isOpen;
        this.chatSoporteService.openChat(this.isOpen);
        if (this.isOpen) {
            this.chatCoordinator.notifyOpened('soporte');
            this.isMinimized = false;
            this.chatSoporteService.minimizedChat(this.isMinimized);
            this.hideNewMessageToast();
            this.scrollToBottom();

            setTimeout(() => {
                if (this.resizer?.nativeElement) {
                    this.resizer.nativeElement.addEventListener('mousedown', this.initResize.bind(this));
                }

                if (this.newWidth > 0 && this.newHeight > 0 && this.chatWidget?.nativeElement) {
                    this.chatWidget.nativeElement.style.width = this.newWidth + 'px';
                    this.chatWidget.nativeElement.style.height = this.newHeight + 'px';
                }
            }, 100);
        } else {
            this.chatCoordinator.notifyClosed('soporte');
        }
    }

    public forceClose(): void {
        if (!this.isOpen) {
            return;
        }
        this.isOpen = false;
        this.isMinimized = false;
        this.chatSoporteService.openChat(false);
        this.chatSoporteService.minimizedChat(false);
        this.chatCoordinator.notifyClosed('soporte');
        this.hideNewMessageToast();
    }

    public close(): void {
        if (this.isOpen) {
            this.forceClose();
        }
    }

    public toggleMinimize(): void {
        this.isMinimized = !this.isMinimized;
        this.chatSoporteService.minimizedChat(this.isMinimized);

        if (this.isMinimized) {
            this.chatWidget.nativeElement.removeAttribute('style');
        } else if (this.newWidth > 0 && this.newHeight > 0) {
            this.chatWidget.nativeElement.style.width = this.newWidth + 'px';
            this.chatWidget.nativeElement.style.height = this.newHeight + 'px';

            this.scrollToBottom();
        } else {
            this.scrollToBottom();
        }
    }

    public shouldShowDateSeparator(index: number): boolean {
        if (index === 0) {
            return true;
        }
        return this.messages[index - 1].date !== this.messages[index].date;
    }

    sendMessage() {
        const text = this.newMessage.trim();
        const file: File | null = this.selectedFile;

        if ((!text && !file) || !this.chatId) {
            return;
        }

        this.newMessage = '';
        this.selectedFile = null;

        const senderName = this.pdv.info.name;
        const senderType = 'pdv';
        const senderId = this.pdvId;

        const payload = {
            id: crypto.randomUUID(),
            chat_id: this.chatId,
            sender_id: senderId,
            sender_type: senderType,
            sender_name: senderName,
            message: text,
            // PdV envía → soporte aún no lo ha leído
            user_support_read: false,
            pdv_read: true,
            file_status: file ? 'pending' : 'none',
            created_at: Timestamp.now()
        };

        this.chatSoporteService.createChatMessage(payload, file).then(() => {
            this.chatSoporteService.sendChatPushNotification(payload, file);

            if (file) {
                this.chatSoporteService.sendChatMessageFile(payload, file).subscribe({
                    next: () => {
                        setTimeout(() => {
                            this.scrollToBottom();
                        }, 500);
                    }
                });
            }
        });
    }

    private scrollToBottom(): void {
        setTimeout(() => {
            const chatScroll = document.querySelector('.chat-window--soporte .messages-scroll') as HTMLElement | null;
            if (chatScroll) {
                chatScroll.scrollTop = chatScroll.scrollHeight;
            }

            this.chatSoporteService.markMessagesAsRead(`${this.chatId}`).then(() => {
                this.unreadMessages = 0;
                this.chatSoporteService.updateUnreadCount(this.unreadMessages);
            });

        }, 100);
    }

    private startRealtimeMessages(): void {
        if (!this.chatId) {
            return;
        }
        if (this.unsubscribeChat) {
            this.unsubscribeChat();
        }
        if (this.unsubscribeMessages) {
            this.unsubscribeMessages();
        }
        this.unsubscribeChat = this.firebaseService.onChat(this.chatId, (chat: any) => {
            if (chat.archived == true) {
                // Cerrar widget y ocultar icono; no re-llamar getChatId (evita bucle)
                this.isOpen = false;
                this.isMinimized = false;
                this.chatSoporteService.openChat(false);
                this.chatSoporteService.minimizedChat(false);
                this.chatCoordinator.notifyClosed('soporte');
                this.hideNewMessageToast();
                this.chatId = null;
                this.chatExists = false;
                this.chatIdLoaded = true;
                this.unreadMessages = 0;
                this.chatSoporteService.updateUnreadCount(0);
                this.chatSoporteService.setChatArchived(true);
                this.unsubscribeChat = undefined;
                this.startRealtimeNewChatListener();
            }
        }, this.config.collectionRoot);
        this.unsubscribeMessages = this.firebaseService.onMessages(this.chatId, (msgs) => {
            const toMillis = (ts: any): number => {
                if (!ts) {
                    return 0;
                }
                if (typeof ts === 'number') {
                    return ts;
                }
                if (ts.seconds != null) {
                    return (ts.seconds * 1000) + Math.floor((ts.nanoseconds || 0) / 1e6);
                }
                const d = new Date(ts);
                return isNaN(d.getTime()) ? 0 : d.getTime();
            };
            const formatTime = (ms: number) => new Date(ms).toLocaleTimeString('es-ES', {hour: '2-digit', minute: '2-digit'});
            const formatDate = (ms: number) => new Date(ms).toLocaleDateString('es-ES');
            const mapSenderType = (senderType: string) => {
                const t = (senderType || '').toString().toLowerCase();
                return t === 'pdv' ? 'PDV' : 'SOPORTE';
            };

            this.messages = msgs
                .sort((a: any, b: any) => toMillis(a.created_at) - toMillis(b.created_at))
                .map((m: any) => {
                    const createdMs = toMillis(m.created_at) || Date.now();
                    const senderType = mapSenderType(m.sender_type ?? '');
                    return {
                        id: m.id ?? m.message_id ?? createdMs,
                        date: formatDate(createdMs),
                        time: formatTime(createdMs),
                        senderType,
                        senderId: m.sender_id,
                        senderName: m.sender_name || (senderType === 'SOPORTE' ? 'Soporte' : 'Usuario'),
                        text: m.message ?? '',
                        file_url: m.file_url ?? '',
                        file_name: m.file_name,
                        file_status: m.file_status,
                        file_is_image: /\.(jpeg|jpg|gif|png|webp|bmp|svg)$/i.test(m.file_url),
                        read: m.pdv_read === true,
                        created_at: m.created_at?.toDate?.() ?? new Date(createdMs),
                    };
                });

            // Solo mensajes de soporte no leídos (no los del propio PdV)
            const actualUnreadMessages = this.messages.filter(
                message => !message.read && message.senderType === 'SOPORTE'
            ).length;

            const previousUnread = this.unreadMessages;
            this.newMessageReceived = actualUnreadMessages > 0 && actualUnreadMessages > previousUnread;
            this.unreadMessages = actualUnreadMessages;

            this.chatSoporteService.updateUnreadCount(this.unreadMessages);

            if (this.realtimeReady) {
                this.showToastTemporarily();
                if (this.isOpen && !this.isMinimized) {
                    this.scrollToBottom();
                }
            } else {
                // Primer snapshot: si ya hay pendientes, mostrar toast (no solo en mensajes nuevos en vivo)
                this.realtimeReady = true;
                if (actualUnreadMessages > 0 && (!this.isOpen || this.isMinimized)) {
                    this.newMessageReceived = true;
                    this.showToastTemporarily();
                }
            }
        }, this.config.collectionRoot);
    }

    private startRealtimeNewChatListener() {
        this.chatSoporteService.onNewChat(this.pdvId, (chat: any) => {
            if (chat && chat.archived !== true) {
                this.chatId = chat.id;
                this.chatExists = true;
                this.chatIdLoaded = true;
                this.chatSoporteService.setChatArchived(false);
                this.startRealtimeMessages();
            }
        });
    }

    public showToastTemporarily(): void {
        if ((this.isOpen && !this.isMinimized) || !this.newMessageReceived) {
            return;
        }
        this.showNewMessageToast = true;
        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
        }
        this.toastTimer = setTimeout(() => {
            this.showNewMessageToast = false;
        }, 7000);
    }

    public hideNewMessageToast(ev?: Event): void {
        if (ev) {
            ev.stopPropagation();
        }
        this.showNewMessageToast = false;
        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
            this.toastTimer = undefined;
        }
    }

    public onToastOpenClick(ev?: Event): void {
        if (ev) {
            ev.stopPropagation();
        }
        this.isOpen = false;
        this.hideNewMessageToast();
        this.toggle();
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.selectedFile = input.files[0];
        }
    }

    openFileSelector() {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '*/*';
        input.onchange = (event: Event) => {
            this.onFileSelected(event);
        };
        input.click();
    }

    public fileIsImage(): boolean {
        return this.selectedFile?.type?.startsWith('image') ?? false;
    }

    public removeFile() {
        this.selectedFile = null;
    }

    public showDeleteMessage(message: any): boolean {
        return this.pdvId == message.senderId && (Date.now() - message.created_at) < 60 * 60 * 1000;
    }

    public requestDeleteMessage(message: any) {
        this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Eliminar mensaje',
                message: '¿Seguro que deseas eliminar este mensaje? Esta acción no se puede deshacer.'
            },
            panelClass: 'home'
        })
            .afterClosed()
            .subscribe((confirm: boolean) => {
                if (confirm) {
                    this.messageToDelete = message.id;
                    this.deleteMessage().then();
                }
            });
    }

    private async deleteMessage() {
        if (!this.chatId || !this.messageToDelete) {
            return;
        }

        await this.chatSoporteService.deleteMessage(this.chatId, this.messageToDelete);
    }
}
